import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useHeaderContext } from '@context';
import { isEnabled } from '@utils/featureFlagsABTests';
import { useGTM, useNewUser } from '@hooks';
import { getUser } from '@utils';
import { Menu, MenuItems } from './components';
import SubHeader from '../SubHeader';
import { HeaderWrapperPropType } from '../../propTypes';

const HeaderWrapper = ({
  children,
  classNames,
  isSticky,
  isMenuItemsOpened,
  onMenuOpened,
}) => {
  const {
    platform,
    isB2B,
    isAuthenticated,
    products = [],
    promos = [],
    basketCount,
    getMenu,
    getBasketCount,
    getPromos,
    featureFlags,
    abTests,
  } = useHeaderContext();
  const [pushToGTM] = useGTM();

  const onToggle = useCallback(() => {
    onMenuOpened(!isMenuItemsOpened);
    if (!isMenuItemsOpened && !isSticky) {
      pushToGTM({
        event: 'click_on_main_navigation',
        event_trigger: 'header',
      });
    }
  }, [isMenuItemsOpened, isSticky]);

  useEffect(() => {
    if (isSticky && isMenuItemsOpened)
      pushToGTM({
        event: 'click_on_main_navigation',
        event_trigger: 'header-lite',
      });
  }, [isSticky, isMenuItemsOpened]);

  const isNewUser = useNewUser();
  const userId = getUser() ? getUser().id : null;
  const isPromosEnabled =
    isEnabled(featureFlags, 'ff-promotion-campaignmanager') &&
    isEnabled(featureFlags, 'ff-promotion-onglet') &&
    isEnabled(abTests, 'promotion-onglet-abtest');

  useEffect(() => {
    getBasketCount();
    getMenu(platform);
    if (isPromosEnabled) {
      getPromos(platform, isB2B, userId, isNewUser);
    }
  }, []);

  return (
    <>
      {children}
      <Menu
        isB2B={isB2B}
        basketCount={basketCount}
        onToggle={onToggle}
        isAuthenticated={isAuthenticated}
        isHidden={isMenuItemsOpened}
        classNames={{
          mobileBasket: classNames.mobileBasket,
          mobileAccount: classNames.mobileAccount,
          mobileMenu: classNames.mobileMenu,
          mobileList: classNames.mobileList,
        }}
      />
      <SubHeader
        classNames={{
          product: classNames.product,
          promo: classNames.promo,
          search: classNames.search,
          contact: classNames.contact,
          account: classNames.account,
          list: classNames.list,
          basket: classNames.basket,
          familiesContainer: classNames.familiesContainer,
        }}
        isSticky={isSticky}
        isMenuItemsOpened={isMenuItemsOpened}
        isPromosEnabled={isPromosEnabled}
      />
      <MenuItems
        isOpen={isMenuItemsOpened}
        tree={products}
        promos={promos}
        isPromosEnabled={isPromosEnabled}
      />
    </>
  );
};

HeaderWrapper.propTypes = {
  children: PropTypes.node.isRequired,
  classNames: HeaderWrapperPropType,
  isSticky: PropTypes.bool,
  isMenuItemsOpened: PropTypes.bool,
  onMenuOpened: PropTypes.func.isRequired,
};

HeaderWrapper.defaultProps = {
  classNames: {},
  isSticky: false,
  isMenuItemsOpened: false,
};

HeaderWrapper.displayName = 'HeaderWrapper';

export default HeaderWrapper;
